import {PRODUCTS_URL, UPLOAD_URL} from "../constants";
import { apiSlice } from "./apiSlice";


export const productsApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getProducts: builder.query({
            query: ({keyword, pageNumber, productType, minPrice, maxPrice, inStock, onSale, color, character} = {}) => {
                // Build query string manually to handle arrays properly
                const params = new URLSearchParams();

                if (keyword) params.append('keyword', keyword);
                if (pageNumber) params.append('pageNumber', pageNumber);

                // Handle productType array - append each one separately
                if (productType && Array.isArray(productType)) {
                    productType.forEach(type => params.append('productType', type));
                } else if (productType) {
                    params.append('productType', productType);
                }

                if (minPrice) params.append('minPrice', minPrice);
                if (maxPrice) params.append('maxPrice', maxPrice);
                if (inStock) params.append('inStock', inStock);
                if (onSale) params.append('onSale', onSale);

                // Handle color array
                if (color && Array.isArray(color)) {
                    color.forEach(c => params.append('color', c));
                } else if (color) {
                    params.append('color', color);
                }

                // Handle character array
                if (character && Array.isArray(character)) {
                    character.forEach(char => params.append('character', char));
                } else if (character) {
                    params.append('character', character);
                }

                return {
                    url: `${PRODUCTS_URL}?${params.toString()}`,
                    credentials: 'include'
                };
            },
            keepUnusedDataFor: 5,
        }),
        getProductsNav: builder.query({
            query: ({keyword, pageNumber} = {}) => ({
                url: PRODUCTS_URL,
                params: {keyword, pageNumber
                }
            }),
            keepUnusedDataFor: 5,
        }),
        getProductDetails: builder.query({
            query: (identifier) => ({
                url: /^[0-9a-fA-F]{24}$/.test(identifier)
                    ? `${PRODUCTS_URL}/${identifier}`
                    : `${PRODUCTS_URL}/slug/${identifier}`,
            }),
            providesTags: ['Products'],
            keepUnusedDataFor: 5,
        }),
        createProduct: builder.mutation({
            query: (data) => ({
                url: PRODUCTS_URL,
                method: "POST",
                body: data,  // ADD THIS
            }),
            invalidatesTags: ['Product'],
        }),
        updateProduct: builder.mutation({
            query: (data) => ({
                url: `${PRODUCTS_URL}/${data.productId}`,
                method: "PUT",
                body: data,
            }),
            invalidatesTags: ['Products'],
        }),
        uploadProductImage: builder.mutation({
            query: (data) => ({
                url: `${UPLOAD_URL}`,
                method: "POST",
                body: data,
            }),
        }),
        deleteProduct: builder.mutation({
            query: (productId) => ({
                url: `${PRODUCTS_URL}/${productId}`,
                method: "DELETE",
            }),
        }),
        createReview: builder.mutation({
            query: (data) => ({
                url: `${PRODUCTS_URL}/${data.productId}/reviews`,
                method: "POST",
                body: data,
            }),
        }),
        getTopProducts: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/top`,
            }),
            keepUnusedDataFor: 5,
        }),
        getFontProducts: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/font`,
            }),
            keepUnusedDataFor: 5,
        }),
        getMugs: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/font/faMugHot`,
            }),
            keepUnusedDataFor: 5,
        }),
        getTeddys: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/font/faTeddyBear`,
            }),
            keepUnusedDataFor: 5,
        }),
        getLamps: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/font/faLampDesk`,
            }),
            keepUnusedDataFor: 5,
        }),

        getMovies: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/font/faFilm`,
            }),
            keepUnusedDataFor: 5,
        }),

        getTVs: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/font/faTvRetro`,
            }),
            keepUnusedDataFor: 5,
        }),

        getTees: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/font/faShirt`,
            }),
            keepUnusedDataFor: 5,
        }),
        getArcade: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/font/faJoystick`,
            }),
            keepUnusedDataFor: 5,
        }),
        getImageProducts: builder.query({
            query: () =>({
                url: `${PRODUCTS_URL}`,
            }),
            keepUnusedDataFor: 5,
        }),
        getMyWishlistQuery: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/wishlist`,
            }),
            keepUnusedDataFor: 5,
        }),
        // getFontProducts: builder.query({
        //     query: () => ({
        //         url: `${PRODUCTS_URL}/font`,
        //     }),
        //     keepUnusedDataFor: 5,
        // }),
        getTopChoices: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/top-choices`,
            }),
            keepUnusedDataFor: 5,
        }),
        getNewArrivals: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/new-arrivals`,
            }),
            keepUnusedDataFor: 5,
        }),
        getClearances: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/clearances`,
            }),
            keepUnusedDataFor: 5,
        }),
        getFeatured: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/featured`,
            }),
            keepUnusedDataFor: 5,
        }),
        getProductTypes: builder.query({
            query: () => '/api/products/product-types',
            keepUnusedDataFor: 5,
        }),
        getUniqueColors: builder.query({
            query: () => '/api/products/filters/colors',
            keepUnusedDataFor: 3600, // Cache for 1 hour since colors don't change often
        }),
        getUniqueCharacters: builder.query({
            query: () => '/api/products/filters/characters',
            keepUnusedDataFor: 3600, // Cache for 1 hour
        }),
        getProductsByGiftCategory: builder.query({
            query: (category) => ({
                url: `${PRODUCTS_URL}/gifts/${category}`,
            }),
            keepUnusedDataFor: 5,
        }),

        getGiftTags: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/gift-tags`,
            }),
            keepUnusedDataFor: 3600,
        }),
        getCarouselSlides: builder.query({
            query: () => '/api/products/carousel-slides',
            keepUnusedDataFor: 60, // Cache for 1 minute
        }),

        getActiveSales: builder.query({
            query: () => '/api/products/active-sales',
            keepUnusedDataFor: 30, // Cache for 30 seconds (sales update frequently)
        }),
        getSpotlight1: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/spotlight/1`,
            }),
            keepUnusedDataFor: 5,
        }),
        getSpotlight2: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/spotlight/2`,
            }),
            keepUnusedDataFor: 5,
        }),
        getSpotlight3: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/spotlight/3`,
            }),
            keepUnusedDataFor: 5,
        }),
        getSpotlight4: builder.query({
            query: () => ({
                url: `${PRODUCTS_URL}/spotlight/4`,
            }),
            keepUnusedDataFor: 5,
        }),
        // userId is only there to keep each signed-in user's answer separate
        getNotifyStatus: builder.query({
            query: ({ productId }) => ({
                url: `${PRODUCTS_URL}/${productId}/notify`,
            }),
            providesTags: ['Products'],
        }),
        addToNotifyList: builder.mutation({
            query: (productId) => ({
                url: `${PRODUCTS_URL}/${productId}/notify`,
                method: 'POST',
            }),
            invalidatesTags: ['Products'],
        }),
        removeFromNotifyList: builder.mutation({
            query: (productId) => ({
                url: `${PRODUCTS_URL}/${productId}/notify`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Products'],
        }),
        createPreOrder: builder.mutation({
            query: ({ productId, shippingAddress }) => ({
                url: `${PRODUCTS_URL}/${productId}/preorder`,
                method: 'POST',
                body: { shippingAddress },
            }),
            invalidatesTags: ['Products'],
        }),
    }),
});

export const { useGetProductsQuery,
    useGetProductDetailsQuery,
    useGetProductsNavQuery,
    useCreateProductMutation,
    useUpdateProductMutation,
    useUploadProductImageMutation,
    useDeleteProductMutation,
    useGetTopProductsQuery,
    useGetFontProductsQuery,
    useGetImageProductsQuery,
    useCreateReviewMutation,
    useGetMyWishlistQuery,
    useGetTopChoicesQuery,
    useGetNewArrivalsQuery,
    useGetClearancesQuery,
    useGetFeaturedQuery,
    useGetMugsQuery,
    useGetTeesQuery,
    useGetTeddysQuery,
    useGetLampsQuery,
    useGetArcadeQuery,
    useGetMoviesQuery,
    useGetTVsQuery,
    useGetProductsByCategoryQuery,
    useGetProductTypesQuery,
    useGetUniqueColorsQuery,
    useGetUniqueCharactersQuery,
    useGetProductsByGiftCategoryQuery,
    useGetGiftTagsQuery,
    useGetCarouselSlidesQuery,
    useGetActiveSalesQuery,
    useGetSpotlight1Query,
    useGetSpotlight2Query,
    useGetSpotlight3Query,
    useGetSpotlight4Query,
    useGetNotifyStatusQuery,
    useAddToNotifyListMutation,
    useRemoveFromNotifyListMutation,
    useCreatePreOrderMutation,
} = productsApiSlice;